import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, GraduationCap, Briefcase, Landmark, Terminal } from 'lucide-react';

const Profile = () => {
    return (
        <section id="perfil" className="py-24 bg-surface relative border-y border-zinc-800">
            <div className="container mx-auto px-6">
                <div className="flex flex-col lg:flex-row gap-16 items-start">

                    {/* Left Column: Story */}
                    <div className="lg:w-1/2">
                        <span className="text-primary font-mono text-sm tracking-widest uppercase mb-4 block">DIRECTOR GENERAL i³</span>
                        <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-8">Trayectoria Global,<br />Impacto Local</h2>

                        <div className="prose prose-invert lg:prose-lg mb-12">
                            <h3 className="text-white text-2xl font-bold mb-4">Ingeniería para el Bien Común</h3>
                            <p className="text-text-muted leading-relaxed mb-6">
                                No soy un político. Soy un emprendedor nacido en Iztacalco que se negó a aceptar el deterioro urbano como destino.
                                Construí soluciones tecnológicas en el extranjero y ahora regreso para poner esa experiencia al servicio de mi comunidad.
                            </p>
                            <blockquote className="border-l-4 border-primary pl-6 italic text-zinc-300 my-8">
                                "Levanté más de $1.3 M USD para startups de tecnología. Escalé equipos de 2 a 20 personas en 3 países.
                                Ahora voy a aplicar esa misma disciplina para arreglar el agua, la seguridad y la corrupción en <strong className="text-white not-italic">Iztacalco</strong>."
                            </blockquote>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <div className="flex gap-4">
                                <div className="bg-zinc-800 p-3 rounded-lg h-fit"><MapPin className="text-primary" size={20} /></div>
                                <div>
                                    <h4 className="font-bold text-white">Origen: Iztacalco</h4>
                                    <p className="text-sm text-zinc-500">Conozco el código postal, la estética y las carencias. Esta es mi casa.</p>
                                </div>
                            </div>
                            <div className="flex gap-4">
                                <div className="bg-zinc-800 p-3 rounded-lg h-fit"><GraduationCap className="text-primary" size={20} /></div>
                                <div>
                                    <h4 className="font-bold text-white">Formación</h4>
                                    <p className="text-sm text-zinc-500">ITAM (Estadística) · Anáhuac (Inteligencia) · NYU (AI).</p>
                                </div>
                            </div>
                            <div className="flex gap-4">
                                <div className="bg-zinc-800 p-3 rounded-lg h-fit"><Briefcase className="text-primary" size={20} /></div>
                                <div>
                                    <h4 className="font-bold text-white">Experiencia</h4>
                                    <p className="text-sm text-zinc-500">Founder Klustera ($1M ARR). VP Eng Culina Health (USA).</p>
                                </div>
                            </div>
                            <div className="flex gap-4">
                                <div className="bg-zinc-800 p-3 rounded-lg h-fit"><Landmark className="text-primary" size={20} /></div>
                                <div>
                                    <h4 className="font-bold text-white">Misión</h4>
                                    <p className="text-sm text-zinc-500">Aplicar excelencia técnica de Silicon Valley a problemas locales.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Credentials (Code Block Style) */}
                    <div className="lg:w-1/2 w-full">
                        <div className="bg-background rounded-xl border border-zinc-800 overflow-hidden shadow-2xl">
                            <div className="bg-zinc-900 px-4 py-2 border-b border-zinc-800 flex items-center gap-2">
                                <Terminal size={14} className="text-zinc-500" />
                                <span className="text-xs font-mono text-zinc-400">pablo_martinez_resume.json</span>
                            </div>
                            <div className="p-6 font-mono text-sm space-y-6">

                                <div className="group">
                                    <span className="text-purple-400 block mb-1">// 01. Machine Learning Expertise</span>
                                    <h4 className="text-white text-lg font-bold mb-1 flex items-center gap-2">
                                        Google Certified ML Engineer <span className="text-xs bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded">2025</span>
                                    </h4>
                                    <p className="text-zinc-500">
                                        Professional Machine Learning Engineer specialized in MLOps, production models, and scaling AI systems.
                                    </p>
                                </div>

                                <div className="group">
                                    <span className="text-blue-400 block mb-1">// 02. Current Roles</span>
                                    <h4 className="text-white text-lg font-bold mb-1">Founder & Lead Engineer, quantified.world</h4>
                                    <p className="text-zinc-500 mb-3">
                                        Google Cloud Certified AI Strategy & Development. We bridge the gap between enterprise security and startup speed.
                                    </p>
                                    <h4 className="text-white text-lg font-bold mb-1">Director General, Instituto i³</h4>
                                    <p className="text-zinc-500">
                                        Think Tank ciudadano para la regeneración urbana de Iztacalco mediante Ciencia de Datos y transparencia radical.
                                    </p>
                                </div>

                                <div className="group">
                                    <span className="text-green-400 block mb-1">// 03. Startup Track Record</span>
                                    <h4 className="text-white text-lg font-bold mb-1">CEO, Klustera</h4>
                                    <p className="text-zinc-500">
                                        $1M USD ARR. Raised $650K+ (VCs/Angels). Scaled teams to 20+ people across USA, Mexico, Chile.
                                    </p>
                                    <h4 className="text-white text-lg font-bold mb-1 mt-3">Co-founder & Chief Product Officer, Evidente AI</h4>
                                    <p className="text-zinc-500">
                                        Building a synthetic data analysis team for e-commerce.
                                    </p>
                                </div>

                                <div className="group">
                                    <span className="text-yellow-400 block mb-1">// 04. Corporate Leadership</span>
                                    <h4 className="text-white text-lg font-bold mb-1">VP Data & Engineering, Culina Health</h4>
                                    <p className="text-zinc-500">
                                        Led data infrastructure and evidence-based decision making for a US HealthTech scaling fast.
                                    </p>
                                </div>

                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
};

export default Profile;
